// Bulk Manual Driver Allocation — Fleet > Vehicles selection bar > "Driver Assignment".
// One dialog, one row per selected vehicle, each with its own searchable driver picker.
// Respects the one-manual-vehicle-per-person rule: already-taken people are hidden and a
// person picked on one row disappears from the others.
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { toast } from 'sonner';
import { Check, ChevronsUpDown, Loader2, UserCog } from 'lucide-react';

const vehicleLabel = (d) => d?.asset_name || d?.name || d?.unit_number || d?.registration || d?.id;

const currentLabel = (d) => {
  if (d?.driver_assignment_mode === 'manual' && d?.manual_assigned_person?.person_name) {
    return `Manual — ${d.manual_assigned_person.person_name}`;
  }
  return 'Auto';
};

export const BulkDriverAssignmentDialog = ({ api, open, onOpenChange, deviceIds, devices, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [pickerOpen, setPickerOpen] = useState(null);

  useEffect(() => {
    if (!open || !deviceIds?.length) return;
    setErrors({});
    const known = new Map((devices || []).map((d) => [d.id, d]));
    const init = (resolve) => setRows(deviceIds.map((id) => {
      const d = resolve(id);
      return d ? {
        device_id: id,
        name: vehicleLabel(d),
        current: currentLabel(d),
        choice: 'keep',
        person: '',
      } : null;
    }).filter(Boolean));
    const missing = deviceIds.filter((id) => !known.has(id));
    if (missing.length === 0) {
      init((id) => known.get(id));
    } else {
      api.get('/fleet/my-devices?asset_type=vehicles&skip=0&limit=1000')
        .then((r) => {
          const all = new Map((r.data?.vehicles || []).map((d) => [d.id, d]));
          init((id) => known.get(id) || all.get(id));
        })
        .catch(() => init((id) => known.get(id)));
    }
    api.get(`/people/options?driver_eligible_only=true&assignable_for_device=${deviceIds.join(',')}`)
      .then((r) => setOptions(r.data || []))
      .catch(() => setOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosen = useMemo(() => new Set(rows.filter((r) => r.choice === 'person' && r.person).map((r) => r.person)), [rows]);

  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    const changed = rows.filter((r) => r.choice !== 'keep');
    if (changed.length === 0) { toast.info('No changes to apply'); return; }
    const missingPerson = changed.find((r) => r.choice === 'person' && !r.person);
    if (missingPerson) { toast.error(`Select a driver for ${missingPerson.name} or set the row back to Keep`); return; }
    const assignments = changed.map((r) => (r.choice === 'auto'
      ? { device_id: r.device_id, mode: 'auto' }
      : { device_id: r.device_id, mode: 'manual', person_type: r.person.split(':')[0], person_id: r.person.split(':')[1] }));
    setSaving(true);
    try {
      const res = await api.put('/fleet/devices/bulk-driver-assignment', { assignments });
      const failed = (res.data?.results || []).filter((x) => !x.ok);
      if (failed.length === 0) {
        toast.success(`Driver assignment updated on ${res.data?.updated ?? assignments.length} vehicle(s)`);
        onOpenChange(false);
        onSaved?.();
      } else {
        const errMap = {};
        failed.forEach((f) => { errMap[f.device_id] = f.error || 'Failed'; });
        setErrors(errMap);
        // Successful rows are done — reset them to Keep so re-Apply only retries failures.
        setRows((prev) => prev.map((r) => {
          if (errMap[r.device_id] || r.choice === 'keep') return r;
          const applied = assignments.find((a) => a.device_id === r.device_id);
          const sel = applied?.mode === 'manual' ? options.find((p) => p.id === applied.person_id) : null;
          return { ...r, choice: 'keep', person: '', current: applied?.mode === 'manual' ? `Manual — ${sel?.name || ''}` : 'Auto' };
        }));
        if ((res.data?.updated ?? 0) > 0) onSaved?.();
        toast.error(`${failed.length} vehicle(s) could not be updated — see rows for details`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk driver assignment failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-driver-assignment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5 text-[#2563eb]" /> Bulk Driver Assignment</DialogTitle>
          <DialogDescription>
            Set a default driver per vehicle, or Auto. A person can be the manual driver of only one vehicle; real RFID/BLE scans always override. Changes apply from the next ignition on.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">{rows.length} vehicle(s) selected</p>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
            onClick={() => setRows((prev) => prev.map((r) => ({ ...r, choice: 'auto', person: '' })))}
            data-testid="bulk-assign-set-all-auto">
            Set all to Auto
          </Button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
          {rows.map((r, i) => {
            const sel = options.find((p) => `${p.person_type}:${p.id}` === r.person);
            return (
              <div key={r.device_id} className="rounded-md border p-2 space-y-1.5" data-testid={`bulk-assign-row-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" data-testid={`bulk-assign-vehicle-${i}`}>{r.name}</p>
                    <p className="text-[10px] text-muted-foreground">Current: {r.current}</p>
                  </div>
                  <div className="flex gap-1">
                    {['keep', 'auto', 'person'].map((c) => (
                      <Button key={c} type="button" size="sm" className="h-7 px-2 text-xs"
                        variant={r.choice === c ? 'default' : 'outline'}
                        onClick={() => setRow(i, { choice: c, ...(c !== 'person' ? { person: '' } : {}) })}
                        data-testid={`bulk-assign-choice-${c}-${i}`}>
                        {c === 'keep' ? 'Keep' : c === 'auto' ? 'Auto' : 'Manual'}
                      </Button>
                    ))}
                  </div>
                </div>
                {r.choice === 'person' && (
                  <Popover open={pickerOpen === i} onOpenChange={(o) => setPickerOpen(o ? i : null)}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" role="combobox" className="w-full h-8 justify-between font-normal text-xs" data-testid={`bulk-assign-person-select-${i}`}>
                        <span className="truncate">{sel ? `${sel.name} — ${sel.person_type === 'driver' ? 'Driver' : 'Staff'}` : 'Select a driver or eligible staff member'}</span>
                        <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 z-[10000] w-[--radix-popover-trigger-width]" align="start">
                      <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
                        <CommandInput placeholder="Search drivers or staff..." data-testid={`bulk-assign-person-search-${i}`} />
                        <CommandList>
                          <CommandEmpty>No available person found</CommandEmpty>
                          <CommandGroup>
                            {options.filter((p) => {
                              const val = `${p.person_type}:${p.id}`;
                              return val === r.person || !chosen.has(val);
                            }).map((p) => {
                              const val = `${p.person_type}:${p.id}`;
                              return (
                                <CommandItem key={val} value={`${p.name} ${p.person_type} ${p.id}`}
                                  onSelect={() => { setRow(i, { person: val }); setPickerOpen(null); }}
                                  data-testid={`bulk-assign-option-${i}-${p.id}`}>
                                  <Check className={`mr-2 h-4 w-4 shrink-0 ${r.person === val ? 'opacity-100' : 'opacity-0'}`} />
                                  <span className="truncate">{p.name}</span>
                                  <span className="ml-auto pl-2 text-[10px] text-muted-foreground shrink-0">{p.person_type === 'driver' ? 'Driver' : 'Staff'}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
                {errors[r.device_id] && (
                  <p className="text-xs text-red-600 dark:text-red-400" data-testid={`bulk-assign-error-${i}`}>{errors[r.device_id]}</p>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-assign-cancel">Cancel</Button>
          <Button type="button" onClick={save} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="bulk-assign-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
